import React, { useContext, useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  TextField, 
  Button, 
  Box, 
  MenuItem, 
  Alert, 
  Paper, 
  Divider, 
  Chip,
  InputAdornment,
  IconButton,
  Stack,
  useMediaQuery,
  useTheme,
  Fade,
  Zoom,
  Avatar
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import EmailIcon from '@mui/icons-material/Email';
import CakeIcon from '@mui/icons-material/Cake';
import WcIcon from '@mui/icons-material/Wc';
import PhoneIcon from '@mui/icons-material/Phone';
import SaveIcon from '@mui/icons-material/Save';
import GoogleIcon from '@mui/icons-material/Google';
import VerifiedIcon from '@mui/icons-material/Verified';
import EditIcon from '@mui/icons-material/Edit';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import PhoneNumberField from '../components/PhoneNumberField';
import PhoneVerification from '../components/PhoneVerification';
import {   
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Storefront as StorefrontIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const UserAccountPage = ({keywordsData, language}) => {
  const { user, updateUser } = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: user?.username || '',
    email: user?.email || '',
    age: user?.age || '',
    sex: user?.sex || '',
    location: user?.location || '',
    user_phone: user?.user_phone || '',
    password: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [showVerification, setShowVerification] = useState(false);
  const [isPhoneVerified, setIsPhoneVerified] = useState(user?.is_phone_verified || false);
  const [isEditingPhone, setIsEditingPhone] = useState(false);

  const handleChange = (field) => (eventOrValue) => {
    let value;
    
    if (typeof eventOrValue === 'object' && eventOrValue.target) {
      value = eventOrValue.target.value;
    } else {
      value = eventOrValue;
    }
    
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (touched[field]) {
      const error = validateField(field, value);
      setErrors(prev => ({ ...prev, [field]: error }));
    }
  };

  const handleUpgradeToShopOwner = () => {
    navigate('/shop-owner-request');
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const validatePhone = (phone) => {
    if (!phone) return language === 'ar' ? 'رقم الهاتف مطلوب' : 'Phone number is required';
    const cleanedPhone = phone.replace(/\s/g, '');
    const regex = /^(\+20|0)?1[0-9]{9}$/;
    if (!regex.test(cleanedPhone)) {
      return language === 'ar' ? 'الرجاء إدخال رقم هاتف مصري صالح' : 'Please enter a valid Egyptian phone number';
    }
    return '';
  };

  const validateEmail = (email) => {
    if (!email) return language === 'ar' ? 'الإيميل مطلوب' : 'Email is required';
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!regex.test(email)) return language === 'ar' ? 'يرجى إدخال عنوان بريد إلكتروني صالح' : 'Please enter a valid email address';
    return '';
  };

  const validatePassword = (password) => {
    if (password && password.length < 6) {
      return language === 'ar' ? 'يجب أن تتكون كلمة المرور من 6 أحرف على الأقل' : 'Password must be at least 6 characters';
    }
    return '';
  };

  const validateConfirmPassword = (confirmPassword) => {
    if (confirmPassword !== formData.newPassword) {
      return language === 'ar' ? 'كلمات المرور غير متطابقة' : 'Passwords do not match';
    }
    return '';
  };

  const validateAge = (age) => {
    if (!age) return language === 'ar' ? 'العمر مطلوب' : 'Age is required';
    if (age && (age < 1 || age > 120)) {
      return language === 'ar' ? 'الرجاء إدخال عمر صالح (1-120)' : 'Please enter a valid age (1-120)';
    }
    return '';
  };

  const validateSex = (sex) => {
    if (!sex || sex === "") return language === 'ar' ? 'نوع الجنس مطلوب' : 'Gender is required';
    return '';
  };

  const validateUsername = (username) => {
    if (!username) return language === 'ar' ? 'اسم المستخدم مطلوب' : 'Username is required';
    if (username.length < 3) {
      return language === 'ar' ? 'اسم المستخدم يجب أن يكون 3 أحرف على الأقل' : 'Username must be at least 3 characters';
    }
    return '';
  };

  const validateField = (field, value) => {
    switch (field) {
      case 'username':
        return validateUsername(value);
      case 'user_phone':
        return validatePhone(value);
      case 'email':
        return validateEmail(value);
      case 'newPassword':
        return validatePassword(value);
      case 'confirmPassword':
        return validateConfirmPassword(value);
      case 'age':
        return validateAge(value);
      case 'sex':
        return validateSex(value);
      default:
        return '';
    }
  };

  const handleBlur = (field) => () => {
    setTouched(prev => ({ ...prev, [field]: true }));
    const error = validateField(field, formData[field]);
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all fields
    const newTouched = {};
    const newErrors = {};
    
    // Fields to validate
    const fieldsToValidate = ['username', 'email', 'age', 'sex'];
    
    // Add phone validation if not verified OR if editing phone
    if (!isPhoneVerified || isEditingPhone) {
      fieldsToValidate.push('user_phone');
    }
    
    fieldsToValidate.forEach(field => {
      newTouched[field] = true;
      const error = validateField(field, formData[field]);
      if (error) newErrors[field] = error;
    });
    
    // Validate password fields if they are filled
    if (formData.newPassword) {
      const passwordError = validatePassword(formData.newPassword);
      if (passwordError) newErrors.newPassword = passwordError;
      newTouched.newPassword = true;
      
      const confirmError = validateConfirmPassword(formData.confirmPassword);
      if (confirmError) newErrors.confirmPassword = confirmError;
      newTouched.confirmPassword = true;
    }
    
    setTouched(newTouched);
    setErrors(newErrors);
    
    if (Object.keys(newErrors).length > 0) {
      setMessage({ 
        type: 'error', 
        text: language === 'ar' 
          ? 'يرجى تصحيح الأخطاء الموجودة في النموذج' 
          : 'Please correct the errors in the form' 
      });
      return;
    }
    
    try {
      // Prepare data for submission
      const submitData = { ...formData };
      
      // Remove empty password fields if not being changed
      if (!submitData.newPassword) {
        delete submitData.newPassword;
        delete submitData.confirmPassword;
        delete submitData.password;
      }
      
      const response = await api.put('/users/update', submitData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      updateUser(response.data.user);
      
      // Update phone verification status from response
      if (response.data.user.is_phone_verified !== undefined) {
        setIsPhoneVerified(response.data.user.is_phone_verified);
      }
      
      // If phone was updated, reset editing mode
      if (isEditingPhone) {
        setIsEditingPhone(false);
        setShowVerification(false);
      }
      
      setMessage({ 
        type: 'success', 
        text: language === 'ar' 
          ? 'تم تحديث الملف الشخصي بنجاح!' 
          : 'Profile updated successfully!' 
      });
      
      setFormData(prev => ({
        ...prev,
        password: '',
        newPassword: '',
        confirmPassword: ''
      }));
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 5000);
      
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || (language === 'ar' ? 'فشل التحديث' : 'Update failed') 
      });
    }
  };

  const handlePhoneVerified = () => {
    setIsPhoneVerified(true);
    setIsEditingPhone(false);
    setShowVerification(false);
    fetchUserData();
  };

  const handleEditPhone = () => {
    setIsEditingPhone(true);
    setShowVerification(false);
    // Clear any phone verification status
    setIsPhoneVerified(false);
  };

  const fetchUserData = async () => {
    try {
      const response = await api.get('/users/me');
      updateUser(response.data.user);
      setFormData(prev => ({
        ...prev,
        user_phone: response.data.user.user_phone || ''
      }));
      // Update verification status from fresh data
      setIsPhoneVerified(response.data.user.is_phone_verified || false);
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  // Check if update button should be enabled
  const isUpdateEnabled = () => {
    // For Google accounts, always enable if basic info is valid
    if (user?.is_google_account) {
      const basicFieldsValid = !validateUsername(formData.username) && 
                               !validateEmail(formData.email) && 
                               !validateAge(formData.age) && 
                               !validateSex(formData.sex);
      return basicFieldsValid;
    }
    
    // For regular accounts, require phone verification or have a valid phone if editing
    if (isPhoneVerified) {
      // Phone is verified, all good
      const allFieldsValid = !validateUsername(formData.username) && 
                             !validateEmail(formData.email) && 
                             !validateAge(formData.age) && 
                             !validateSex(formData.sex);
      return allFieldsValid;
    } else {
      // Phone not verified - need valid phone number
      const phoneValid = formData.user_phone && !validatePhone(formData.user_phone);
      const basicFieldsValid = !validateUsername(formData.username) && 
                               !validateEmail(formData.email) && 
                               !validateAge(formData.age) && 
                               !validateSex(formData.sex);
      return basicFieldsValid && phoneValid;
    }
  };

  return (
    <Container maxWidth="md" sx={{ 
      py: { xs: 2, sm: 3, md: 4 }, 
      px: { xs: 1.5, sm: 2, md: 3 },
      mb: 10,
      minHeight: '100vh'
    }}>
      {/* Header */}
      <Zoom in>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2, 
          mb: { xs: 2, sm: 3, md: 4 },
          flexDirection: { xs: 'column', sm: 'row' },
          textAlign: { xs: 'center', sm: 'left' }
        }}>
          <Avatar sx={{ 
            width: { xs: 56, sm: 64, md: 80 }, 
            height: { xs: 56, sm: 64, md: 80 },
            bgcolor: '#FFD700',
            color: '#000000'
          }}>
            <AccountCircleIcon sx={{ fontSize: { xs: 32, sm: 40, md: 48 } }} />
          </Avatar>
          <Box>
            <Typography variant={isMobile ? "h4" : "h3"} sx={{ 
              color: '#FFD700',
              fontWeight: 900,
              letterSpacing: '2px'
            }}>
              {language === 'ar' ? 'الحساب الشخصي' : 'My Account'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#B3B3B3', mt: 0.5 }}>
              {language === 'ar' ? 'إدارة معلوماتك الشخصية' : 'Manage your personal information'}
            </Typography>
          </Box>
        </Box>
      </Zoom>

      {/* Welcome Card */}
      <Fade in>
        <Paper sx={{ 
          p: { xs: 2, sm: 3 }, 
          mb: { xs: 2, sm: 3, md: 4 },
          background: 'linear-gradient(135deg, #0D0D0D 0%, #1A1A1A 100%)',
          border: '1px solid #FFD700',
          borderRadius: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1.5, sm: 2 },
          flexDirection: { xs: 'column', sm: 'row' },
          textAlign: { xs: 'center', sm: 'left' }
        }}>
          <Avatar sx={{ 
            width: { xs: 48, sm: 56 }, 
            height: { xs: 48, sm: 56 }, 
            bgcolor: 'rgba(255, 215, 0, 0.1)'
          }}>
            <AccountCircleIcon sx={{ fontSize: { xs: 28, sm: 32 }, color: '#FFD700' }} />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ color: '#FFD700', fontWeight: 700 }}>
              {language === 'ar' ? 'مرحباً، ' : 'Welcome, '} {formData.username}!
            </Typography>
            <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
              {language === 'ar' 
                ? 'يمكنك تحديث معلوماتك الشخصية من هنا' 
                : 'You can update your personal information here'}
            </Typography>
          </Box>
        </Paper>
      </Fade>

      {/* Upgrade to Shop Owner Button */}
      {user?.role === 'user'  && (
        <Paper sx={{ 
          p: 3, 
          mb: 4,
          backgroundColor: 'rgba(255, 215, 0, 0.05)',
          border: '2px solid #FFD700',
          borderRadius: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <StorefrontIcon sx={{ fontSize: 40, color: '#FFD700' }} />
            <Box>
              <Typography variant="h6" sx={{ color: '#FFD700', fontWeight: 700 }}>
                {language === 'ar' ? 'كن صاحب متجر' : 'Become a store Owner'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                {language === 'ar' 
                  ? 'قم بتقديم طلب لفتح متجرك الخاص والبدء في بيع منتجاتك' 
                  : 'Submit a request to open your own store and start selling your products'}
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<StorefrontIcon />}
            onClick={handleUpgradeToShopOwner}
            sx={{
              backgroundColor: '#FFD700',
              color: '#000000',
              fontWeight: 800,
              py: 1.5,
              px: 3,
              borderRadius: '12px',
              border: '1px solid #FFD700',
              '&:hover': {
                backgroundColor: '#FFE55C',
                transform: 'translateY(-2px)'
              },
              transition: 'all 0.2s ease'
            }}
          >
            {language === 'ar' ? 'تقديم طلب' : 'Submit Request'}
          </Button>
        </Paper>
      )}

      {/* Message Alerts */}
      {message.text && (
        <Fade in>
          <Alert 
            severity={message.type} 
            sx={{ 
              mb: 3,
              backgroundColor: message.type === 'success' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
              color: message.type === 'success' ? '#4CAF50' : '#F44336',
              border: `1px solid ${message.type === 'success' ? '#4CAF50' : '#F44336'}`,
              borderRadius: '12px',
              '& .MuiAlert-icon': {
                color: message.type === 'success' ? '#4CAF50' : '#F44336',
              }
            }}
          >
            {message.text}
          </Alert>
        </Fade>
      )}

      {/* Main Form Card */}
      <Paper sx={{ 
        p: { xs: 2, sm: 3, md: 4 },
        background: 'linear-gradient(135deg, #0D0D0D 0%, #121212 100%)',
        border: '1px solid #2D2D2D',
        borderRadius: '24px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        transition: 'all 0.3s ease',
        '&:hover': {
          borderColor: '#FFD700',
          boxShadow: '0 12px 40px rgba(255, 215, 0, 0.15)',
        }
      }}>
        <Box component="form" onSubmit={handleSubmit}>
          {/* Personal Information Section */}
          <Typography variant="h6" sx={{ 
            color: '#FFD700',
            fontWeight: 700,
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <AccountCircleIcon sx={{ color: '#FFD700' }} /> 
            {language === 'ar' ? 'المعلومات الشخصية' : 'Personal Information'}
          </Typography>

          <Divider sx={{ 
            backgroundColor: '#2D2D2D',
            height: '1px',
            mb: 3
          }} />

          <Stack spacing={3}>
            {/* Username */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AccountCircleIcon sx={{ fontSize: 20, color: '#B3B3B3' }} />
                <Typography sx={{ color: '#B3B3B3', fontSize: '0.9rem', fontWeight: 500 }}>
                  {language === 'ar' ? 'اسم المستخدم' : 'Username'} 
                  <Typography component="span" sx={{ color: '#F44336', ml: 0.5 }}>*</Typography>
                </Typography>
              </Box>
              <TextField
                fullWidth
                placeholder={language === 'ar' ? 'أدخل اسم المستخدم' : 'Enter username'} 
                value={formData.username}
                onChange={handleChange('username')}
                onBlur={handleBlur('username')}
                error={touched.username && !!errors.username}
                helperText={touched.username && errors.username}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#1A1A1A',
                    color: '#FFFFFF',
                    '& fieldset': { borderColor: '#2D2D2D' },
                    '&:hover fieldset': { borderColor: '#FFD700' },
                    '&.Mui-focused fieldset': { borderColor: '#FFD700' },
                  },
                  '& .MuiFormHelperText-root': {
                    color: '#F44336',
                  },
                }}
              />
            </Box>

            {/* Email - Disabled for Google accounts */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                <EmailIcon sx={{ fontSize: 20, color: '#B3B3B3' }} />
                <Typography sx={{ color: '#B3B3B3', fontSize: '0.9rem', fontWeight: 500 }}>
                  {language === 'ar' ? 'البريد الإلكتروني' : 'Email'}
                  <Typography component="span" sx={{ color: '#F44336', ml: 0.5 }}>*</Typography>
                </Typography>
                {user?.is_google_account && (
                  <Chip 
                    icon={<GoogleIcon sx={{ fontSize: 14 }} />}
                    label="Google"
                    size="small"
                    sx={{
                      backgroundColor: 'rgba(255, 215, 0, 0.1)',
                      color: '#FFD700',
                      border: '1px solid #FFD700',
                      height: 24
                    }}
                  />
                )}
              </Box>
              <TextField
                fullWidth
                placeholder={language === 'ar' ? 'البريد الإلكتروني' : 'Email'}
                type="email"
                value={formData.email}
                onChange={handleChange('email')}
                onBlur={handleBlur('email')}
                error={touched.email && !!errors.email}
                helperText={touched.email && errors.email}
                required
                disabled={user?.is_google_account}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: user?.is_google_account ? '#0D0D0D' : '#1A1A1A',
                    color: user?.is_google_account ? '#6B6B6B' : '#FFFFFF',
                    '& fieldset': { borderColor: '#2D2D2D' },
                    ...(user?.is_google_account ? {} : {
                      '&:hover fieldset': { borderColor: '#FFD700' },
                      '&.Mui-focused fieldset': { borderColor: '#FFD700' },
                    })
                  },
                }}
              />
            </Box>

            {/* Age & Sex Grid */}
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, 
              gap: 2 
            }}>
              {/* Age */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CakeIcon sx={{ fontSize: 20, color: '#B3B3B3' }} />
                  <Typography sx={{ color: '#B3B3B3', fontSize: '0.9rem', fontWeight: 500 }}>
                    {language === 'ar' ? 'العمر' : 'Age'}
                    <Typography component="span" sx={{ color: '#F44336', ml: 0.5 }}>*</Typography>
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  placeholder={language === 'ar' ? 'العمر' : 'Age'}
                  type="number"
                  value={formData.age}
                  onChange={handleChange('age')}
                  onBlur={handleBlur('age')}
                  error={touched.age && !!errors.age}
                  helperText={touched.age && errors.age}
                  inputProps={{ min: 1, max: 120 }}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#1A1A1A',
                      color: '#FFFFFF',
                      '& fieldset': { borderColor: '#2D2D2D' },
                      '&:hover fieldset': { borderColor: '#FFD700' },
                      '&.Mui-focused fieldset': { borderColor: '#FFD700' },
                    },
                  }}
                />
              </Box>

              {/* Sex */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <WcIcon sx={{ fontSize: 20, color: '#B3B3B3' }} />
                  <Typography sx={{ color: '#B3B3B3', fontSize: '0.9rem', fontWeight: 500 }}>
                    {language === 'ar' ? 'الجنس' : 'Gender'}
                    <Typography component="span" sx={{ color: '#F44336', ml: 0.5 }}>*</Typography>
                  </Typography>
                </Box>
                <TextField
                  select
                  fullWidth
                  value={formData.sex}
                  onChange={handleChange('sex')}
                  onBlur={handleBlur('sex')}
                  error={touched.sex && !!errors.sex}
                  helperText={touched.sex && errors.sex}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#1A1A1A',
                      color: '#FFFFFF',
                      '& fieldset': { borderColor: '#2D2D2D' },
                      '&:hover fieldset': { borderColor: '#FFD700' },
                      '&.Mui-focused fieldset': { borderColor: '#FFD700' },
                    },
                  }}
                >
                  <MenuItem value="" sx={{ color: '#B3B3B3' }}>
                    {language === 'ar' ? 'اختر الجنس' : 'Select gender'}
                  </MenuItem>
                  <MenuItem value="male" sx={{ color: '#FFFFFF' }}>
                    {language === 'ar' ? 'ذكر' : 'Male'}
                  </MenuItem>
                  <MenuItem value="female" sx={{ color: '#FFFFFF' }}>
                    {language === 'ar' ? 'أنثى' : 'Female'}
                  </MenuItem>
                </TextField>
              </Box>
            </Box>

            {/* Phone Number with Verification */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                <PhoneIcon sx={{ fontSize: 20, color: '#B3B3B3' }} />
                <Typography sx={{ color: '#B3B3B3', fontSize: '0.9rem', fontWeight: 500 }}>
                  {language === 'ar' ? 'رقم الهاتف' : 'Phone Number'}
                  {!isPhoneVerified && !isEditingPhone && (
                    <Typography component="span" sx={{ color: '#F44336', ml: 0.5 }}>*</Typography>
                  )}
                </Typography>
                {isPhoneVerified && !isEditingPhone && (
                  <Chip
                    icon={<VerifiedIcon sx={{ fontSize: 14 }} />}
                    label={language === 'ar' ? 'موثق' : 'Verified'}
                    size="small"
                    sx={{
                      backgroundColor: '#4CAF50',
                      color: '#FFFFFF',
                      fontWeight: 600
                    }}
                  />
                )}
                {isPhoneVerified && !isEditingPhone && (
                  <Button
                    size="small"
                    onClick={handleEditPhone}
                    startIcon={<EditIcon sx={{ fontSize: 14 }} />}
                    sx={{
                      color: '#FFD700',
                      textTransform: 'none',
                      fontSize: '0.7rem',
                      '&:hover': { backgroundColor: 'rgba(255, 215, 0, 0.08)' }
                    }}
                  >
                    {language === 'ar' ? 'تغيير' : 'Change'}
                  </Button>
                )}
                {!isPhoneVerified && !isEditingPhone && formData.user_phone && (
                  <Button
                    size="small"
                    onClick={() => setShowVerification(!showVerification)}
                    sx={{
                      color: '#FFD700',
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      '&:hover': { backgroundColor: 'rgba(255, 215, 0, 0.08)' }
                    }}
                  >
                    {showVerification 
                      ? (language === 'ar' ? 'إخفاء' : 'Hide')
                      : (language === 'ar' ? 'توثيق' : 'Verify')}
                  </Button>
                )}
              </Box>
              
              <PhoneNumberField
                fullWidth
                placeholder={language === 'ar' ? 'أدخل رقم الهاتف' : 'Enter phone number'}
                value={formData.user_phone}
                onChange={handleChange('user_phone')}
                onBlur={handleBlur('user_phone')}
                error={touched.user_phone && !!errors.user_phone}
                helperText={touched.user_phone && errors.user_phone}
                lang={language}
                required={!isPhoneVerified && !isEditingPhone}
                disabled={isPhoneVerified && !isEditingPhone}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: (isPhoneVerified && !isEditingPhone) ? '#0D0D0D' : '#1A1A1A',
                    color: (isPhoneVerified && !isEditingPhone) ? '#6B6B6B' : '#FFFFFF',
                    '& fieldset': { 
                      borderColor: (isPhoneVerified && !isEditingPhone) ? '#4CAF50' : '#2D2D2D' 
                    },
                    ...((isPhoneVerified && !isEditingPhone) ? {} : {
                      '&:hover fieldset': { borderColor: '#FFD700' },
                      '&.Mui-focused fieldset': { borderColor: '#FFD700' },
                    })
                  },
                }}
              />
              
              {/* Phone Verification Component */}
              {showVerification && !isPhoneVerified && (
                <Box sx={{ mt: 2 }}>
                  <PhoneVerification 
                    language={language}
                    onVerified={handlePhoneVerified}
                    currentPhoneNumber={formData.user_phone}
                  />
                </Box>
              )}
            </Box>
          </Stack>

          {/* Password Section - Only for non-Google accounts */}
          {!user?.is_google_account && (
            <>
              <Typography variant="h6" sx={{ 
                color: '#FFD700',
                fontWeight: 700,
                mb: 2,
                mt: 3,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <LockIcon sx={{ color: '#FFD700' }} /> 
                {language === 'ar' ? 'تغيير كلمة المرور' : 'Change Password'}
              </Typography>

              <Divider sx={{ 
                backgroundColor: '#2D2D2D',
                height: '1px',
                mb: 3
              }} />

              <Stack spacing={3}>
                {/* Current Password */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <LockIcon sx={{ fontSize: 20, color: '#B3B3B3' }} />
                    <Typography sx={{ color: '#B3B3B3', fontSize: '0.9rem', fontWeight: 500 }}>
                      {language === 'ar' ? 'كلمة المرور الحالية' : 'Current Password'}
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    placeholder={language === 'ar' ? 'أدخل كلمة المرور الحالية' : 'Enter current password'}
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange('password')}
                    onBlur={handleBlur('password')}
                    error={touched.password && !!errors.password}
                    helperText={touched.password && errors.password}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon sx={{ color: '#B3B3B3' }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#1A1A1A',
                        color: '#FFFFFF',
                        '& fieldset': { borderColor: '#2D2D2D' },
                        '&:hover fieldset': { borderColor: '#FFD700' },
                        '&.Mui-focused fieldset': { borderColor: '#FFD700' },
                      },
                    }}
                  />
                </Box>

                {/* New Password */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <LockIcon sx={{ fontSize: 20, color: '#B3B3B3' }} />
                    <Typography sx={{ color: '#B3B3B3', fontSize: '0.9rem', fontWeight: 500 }}>
                      {language === 'ar' ? 'كلمة المرور الجديدة' : 'New Password'}
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    placeholder={language === 'ar' ? 'أدخل كلمة المرور الجديدة' : 'Enter new password'}
                    type={showPassword ? 'text' : 'password'}
                    value={formData.newPassword}
                    onChange={handleChange('newPassword')}
                    onBlur={handleBlur('newPassword')}
                    error={touched.newPassword && !!errors.newPassword}
                    helperText={touched.newPassword && errors.newPassword}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon sx={{ color: '#B3B3B3' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#1A1A1A',
                        color: '#FFFFFF',
                        '& fieldset': { borderColor: '#2D2D2D' },
                        '&:hover fieldset': { borderColor: '#FFD700' },
                        '&.Mui-focused fieldset': { borderColor: '#FFD700' },
                      },
                    }}
                  />
                  <Typography variant="caption" sx={{ color: '#B3B3B3', mt: 0.5, display: 'block' }}>
                    {language === 'ar' 
                      ? 'كلمة المرور يجب أن تكون 6 أحرف على الأقل' 
                      : 'Password must be at least 6 characters'}
                  </Typography>
                </Box>

                {/* Confirm Password */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <LockIcon sx={{ fontSize: 20, color: '#B3B3B3' }} />
                    <Typography sx={{ color: '#B3B3B3', fontSize: '0.9rem', fontWeight: 500 }}>
                      {language === 'ar' ? 'تأكيد كلمة المرور' : 'Confirm Password'}
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    placeholder={language === 'ar' ? 'أعد إدخال كلمة المرور الجديدة' : 'Confirm new password'}
                    type={showPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={handleChange('confirmPassword')}
                    onBlur={handleBlur('confirmPassword')}
                    error={touched.confirmPassword && !!errors.confirmPassword}
                    helperText={touched.confirmPassword && errors.confirmPassword}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon sx={{ color: '#B3B3B3' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#1A1A1A',
                        color: '#FFFFFF',
                        '& fieldset': { borderColor: '#2D2D2D' },
                        '&:hover fieldset': { borderColor: '#FFD700' },
                        '&.Mui-focused fieldset': { borderColor: '#FFD700' },
                      },
                    }}
                  />
                </Box>
              </Stack>
            </>
          )}

          {/* Google Account Notice */}
          {user?.is_google_account && (
            <Paper sx={{ 
              p: 2, 
              mb: 2,
              backgroundColor: 'rgba(255, 215, 0, 0.05)',
              border: '1px solid #FFD700',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexDirection: { xs: 'column', sm: 'row' },
              textAlign: { xs: 'center', sm: 'left' }
            }}>
              <GoogleIcon sx={{ color: '#FFD700', fontSize: 28 }} />
              <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                {language === 'ar' 
                  ? 'أنت متصل بحساب Google. لا يمكن تغيير كلمة المرور لحسابات Google.'
                  : 'You are logged in with Google. Password cannot be changed for Google accounts.'}
              </Typography>
            </Paper>
          )}

          {/* Submit Button - Always visible but conditionally enabled */}
          <Button 
            type="submit" 
            variant="contained" 
            size="large"
            fullWidth
            startIcon={<SaveIcon />}
            disabled={!isUpdateEnabled()}
            sx={{ 
              mt: 3,
              py: { xs: 1.5, sm: 1.8 },
              backgroundColor: '#FFD700',
              color: '#000000',
              fontWeight: 800,
              fontSize: { xs: '1rem', sm: '1.1rem' },
              borderRadius: '12px',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#FFE55C',
                boxShadow: '0 6px 20px rgba(255, 215, 0, 0.4)',
                transform: 'translateY(-2px)'
              },
              '&.Mui-disabled': {
                backgroundColor: '#2D2D2D',
                color: '#6B6B6B'
              },
              transition: 'all 0.2s ease'
            }}
          >
            {language === 'ar' ? 'تحديث الحساب' : 'Update Account'}
          </Button>

          {/* Warning if phone not verified */}
          {!isPhoneVerified && !user?.is_google_account && !isEditingPhone && formData.user_phone && (
            <Alert 
              severity="warning" 
              sx={{ 
                mt: 3,
                backgroundColor: 'rgba(255, 152, 0, 0.1)',
                color: '#FF9800',
                border: '1px solid #FF9800',
                borderRadius: '12px'
              }}
            >
              {language === 'ar' 
                ? '⚠️ يرجى توثيق رقم الهاتف لتتمكن من تحديث الملف الشخصي'
                : '⚠️ Please verify your phone number to update your profile'}
            </Alert>
          )}
          
          {!formData.user_phone && !isPhoneVerified && !user?.is_google_account && (
            <Alert 
              severity="info" 
              sx={{ 
                mt: 3,
                backgroundColor: 'rgba(255, 215, 0, 0.05)',
                color: '#FFD700',
                border: '1px solid #FFD700',
                borderRadius: '12px'
              }}
            >
              {language === 'ar' 
                ? '📱 يرجى إضافة رقم هاتف وتوثيقه لتتمكن من تحديث الملف الشخصي'
                : '📱 Please add and verify your phone number to update your profile'}
            </Alert>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default UserAccountPage;